import { Modal } from '@components/ui';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { useSupportHelp } from '@features/support/hooks/useSupportHelp';
import { SupportAssistantPanel } from '@features/support/components/SupportAssistantPanel';

interface ErrorDetail {
  code: string;
  message: string;
}

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  details?: ErrorDetail[];
  // Si se activa, consulta al agente de soporte y muestra "Asistente IA" inline.
  assist?: boolean;
  screen?: string; // pantalla/acción donde ocurrió (ej. "emisión de factura")
}

export function ErrorModal({ isOpen, onClose, title = 'Error', message, details, assist = false, screen }: ErrorModalProps) {
  const help = useSupportHelp({
    enabled: isOpen && assist && !!message,
    message: [message, ...(details || []).map((d) => `${d.code}: ${d.message}`)].join('\n'),
    errorCodes: (details || []).map((d) => d.code).filter(Boolean),
    screen: screen || title,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
    >
      <div className="space-y-4">
        {/* Error Icon and Main Message */}
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <XCircleIcon className="h-8 w-8 text-danger-500" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-secondary-700 dark:text-secondary-300">
              {message}
            </p>
          </div>
        </div>

        {/* Error Details List */}
        {details && details.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-secondary-900 dark:text-white mb-2">
              Detalles del error:
            </h4>
            <div className="space-y-2">
              {details.map((detail, index) => (
                <div
                  key={index}
                  className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-3"
                >
                  <div className="flex items-start space-x-2">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-danger-100 dark:bg-danger-900/40 text-danger-800 dark:text-danger-200 text-xs font-medium flex-shrink-0 mt-0.5">
                      {detail.code}
                    </span>
                    <p className="text-sm text-danger-800 dark:text-danger-200 flex-1">
                      {detail.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Asistente IA (si está habilitado) */}
        {assist && <SupportAssistantPanel help={help} />}

        {/* Actions */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-danger-600 hover:bg-danger-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
}
